import { apiClient } from './client';
import { Agreement, ApiResponse, PublicAgreementView } from '@/types';

export const agreementsApi = {
  // List agreements scoped to a report (used by ReportBuilder for the
  // status indicator). Same shape will work for appointmentId once the
  // booking-widget MR lands.
  listForReport: async (reportId: string) =>
    apiClient.get<ApiResponse<Agreement[]>>(`/agreements?reportId=${encodeURIComponent(reportId)}`),

  // List agreements scoped to an appointment — used by the inspection page
  // (#79) to surface agreement state when the inspection is linked to a
  // scheduled appointment.
  listForAppointment: async (appointmentId: string) =>
    apiClient.get<ApiResponse<Agreement[]>>(
      `/agreements?appointmentId=${encodeURIComponent(appointmentId)}`
    ),

  // Send a pre-inspection agreement for a report. The backend pulls
  // client_name + client_email from the report row by default; overrides
  // are accepted but rarely used in the report-side flow.
  createForReport: async (
    reportId: string,
    overrides: { clientName?: string; clientEmail?: string } = {}
  ) =>
    apiClient.post<
      ApiResponse<{
        agreement: Agreement;
        signingUrl: string;
        emailSent: boolean;
        emailError: string | null;
      }>
    >('/agreements', { reportId, ...overrides }),

  // Send a pre-inspection agreement for an appointment. The backend pulls
  // client_name + client_email from the appointment row.
  createForAppointment: async (
    appointmentId: string,
    overrides: { clientName?: string; clientEmail?: string } = {}
  ) =>
    apiClient.post<
      ApiResponse<{
        agreement: Agreement;
        signingUrl: string;
        emailSent: boolean;
        emailError: string | null;
      }>
    >('/agreements', { appointmentId, ...overrides }),

  // Public, unauthenticated. Used by SigningPage to load the agreement.
  getByToken: async (token: string) =>
    apiClient.get<ApiResponse<PublicAgreementView>>(
      `/agreements/public/${encodeURIComponent(token)}`
    ),

  // Public, unauthenticated. Submits the captured signature.
  signByToken: async (token: string, signatureData: string) =>
    apiClient.post<
      ApiResponse<{
        signedPdfUrl: string;
        signedAt?: string;
        clientEmailSent?: boolean;
        inspectorEmailSent?: boolean;
        alreadySigned: boolean;
      }>
    >(`/agreements/public/${encodeURIComponent(token)}/sign`, { signatureData }),
};
